import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import {
  isIntermediateWidth,
  isMobileLayout,
  onResponsiveMediaChange
} from '../core/responsive-mode.js';

/**
 * Front-my-End - Mobile View, Tab Navigation & Layout View Override Features
 */

const MOBILE_TABS = ['html', 'css', 'js', 'preview', 'console'];
const SWIPE_MIN_DISTANCE = 64;
const SWIPE_MAX_VERTICAL_DRIFT = 96;
const SWIPE_DOMINANCE_RATIO = 1.35;
let swipeRegistered = false;
let swipeState = null;

export function registerResponsiveFeature() {
  const onChange = () => applyResponsiveMode();
  onResponsiveMediaChange(onChange);

  ensureMobileSwipeEdges();
  registerMobileSwipeNavigation();

  // Subscribe to forcedView updates to immediately update layout classes
  store.subscribe('forcedView', () => {
    applyResponsiveMode();
  });

  // Subscribe to activeTab in store to drive DOM changes
  store.subscribe('activeTab', (tab) => {
    if (tab) {
      applyActiveMobileTab(tab);
    }
  });

  // Wire tab buttons to store updates
  MOBILE_TABS.forEach((tab) => {
    const btn = document.getElementById(`tab-${tab}`);
    if (btn) {
      btn.addEventListener('click', () => {
        store.set('activeTab', tab);
      });
    }
  });
}

/**
 * Returns true if the mobile layout view is currently active.
 * Takes user forced view overrides into account.
 */
export function isMobile() {
  return isMobileLayout();
}

/**
 * Governs the HTML body classes based on media queries and manual store overrides.
 */
export function applyResponsiveMode() {
  const useMobile = isMobile();
  const mobileContextFab = refs.mobileContextFab;

  if (useMobile) {
    document.body.classList.add('mobile-view');
    document.body.classList.remove('desktop-view');
    if (mobileContextFab) {
      mobileContextFab.hidden = false;
    }
    
    // Ensure an active mobile tab is selected
    if (!store.get('activeTab')) {
      store.set('activeTab', 'html');
    }
  } else {
    document.body.classList.add('desktop-view');
    document.body.classList.remove('mobile-view');
    if (mobileContextFab) {
      mobileContextFab.hidden = true;
    }
  }

  // Handle intermediate scale when desktop view is active and screen width is narrow
  const isNarrow = isIntermediateWidth();
  if (!useMobile && isNarrow) {
    document.body.classList.add('intermediate-width');
  } else {
    document.body.classList.remove('intermediate-width');
  }
}

function applyActiveMobileTab(name) {
  MOBILE_TABS.forEach((tab) => {
    const tabBtn = document.getElementById(`tab-${tab}`);
    const panel = document.getElementById(`panel-${tab}`);
    if (tabBtn) tabBtn.classList.remove('active');
    if (panel) panel.classList.remove('mobile-active');
  });

  const activeBtn = document.getElementById(`tab-${name}`);
  const activePanel = document.getElementById(`panel-${name}`);
  if (activeBtn) activeBtn.classList.add('active');
  if (activePanel) activePanel.classList.add('mobile-active');
}

function ensureMobileSwipeEdges() {
  MOBILE_TABS.forEach((tab) => {
    const panel = document.getElementById(`panel-${tab}`);
    if (!panel || panel.querySelector('.mobile-swipe-edge')) return;

    ['left', 'right'].forEach((side) => {
      const edge = document.createElement('div');
      edge.className = `mobile-swipe-edge ${side}`;
      edge.setAttribute('aria-hidden', 'true');
      panel.appendChild(edge);
    });
  });
}

function registerMobileSwipeNavigation() {
  const workspace = document.getElementById('workspace');
  if (!workspace || swipeRegistered) return;
  swipeRegistered = true;

  const resetSwipe = () => {
    swipeState = null;
  };

  workspace.addEventListener('pointerdown', (event) => {
    if (!isMobile()) return;
    if (event.pointerType && event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (typeof event.button === 'number' && event.button > 0) return;
    if (!isSwipeCandidate(event.target)) return;

    swipeState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY
    };

    try {
      workspace.setPointerCapture(event.pointerId);
    } catch (err) {}
  }, { passive: true });

  workspace.addEventListener('pointermove', (event) => {
    if (!swipeState || event.pointerId !== swipeState.pointerId) return;

    swipeState.currentX = event.clientX;
    swipeState.currentY = event.clientY;

    const deltaX = event.clientX - swipeState.startX;
    const deltaY = event.clientY - swipeState.startY;
    if (Math.abs(deltaX) > 18 && Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_DOMINANCE_RATIO) {
      event.preventDefault();
    }
  }, { passive: false });

  window.addEventListener('pointerup', (event) => {
    if (!swipeState || event.pointerId !== swipeState.pointerId) return;

    const state = swipeState;
    resetSwipe();

    const endX = Number.isFinite(event.clientX) ? event.clientX : state.currentX;
    const endY = Number.isFinite(event.clientY) ? event.clientY : state.currentY;
    const deltaX = endX - state.startX;
    const deltaY = endY - state.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (
      absX >= SWIPE_MIN_DISTANCE &&
      absY <= SWIPE_MAX_VERTICAL_DRIFT &&
      absX > absY * SWIPE_DOMINANCE_RATIO
    ) {
      moveMobileTab(deltaX < 0 ? 1 : -1);
    }
  });

  window.addEventListener('pointercancel', resetSwipe);
}

function isSwipeCandidate(target) {
  if (!(target instanceof Element)) return true;
  if (target.closest('#panel-scratchpad:not(.hidden-panel), dialog, #quick-menu-dropdown.active, .context-menu')) {
    return false;
  }
  return !target.closest('button, input, select, [contenteditable="true"]');
}

function moveMobileTab(delta) {
  if (!isMobile()) return;

  const current = store.get('activeTab') || MOBILE_TABS[0];
  const currentIndex = Math.max(0, MOBILE_TABS.indexOf(current));
  const nextIndex = Math.min(MOBILE_TABS.length - 1, Math.max(0, currentIndex + delta));
  if (nextIndex !== currentIndex) {
    store.set('activeTab', MOBILE_TABS[nextIndex]);
  }
}
